import { filterBranchLocations } from './destinationType.js';
import {
  resolveAllowedDestinationHubIds,
  filterToLocationsByDestinationMapping,
} from './destinationHubMapping.js';

/** Normalize hub.dispatch_types to uppercase OGCL/ITCL list (default OGCL). */
export function normalizeHubDispatchTypes(hubOrTypes) {
  const raw = hubOrTypes && typeof hubOrTypes === 'object' && !Array.isArray(hubOrTypes)
    ? hubOrTypes.dispatch_types
    : hubOrTypes;
  if (raw == null || raw === '') return ['OGCL'];
  const list = Array.isArray(raw)
    ? raw
    : String(raw)
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
  const normalized = [];
  for (const item of list) {
    const type = String(item || '').trim().toUpperCase();
    if ((type === 'OGCL' || type === 'ITCL') && !normalized.includes(type)) {
      normalized.push(type);
    }
  }
  return normalized.length ? normalized : ['OGCL'];
}

/** Union of dispatch types for selected From hub ids. */
export function unionDispatchTypesForHubIds(hubs, hubIds) {
  const selected = new Set((hubIds || []).map(String));
  const types = new Set();
  for (const hub of hubs || []) {
    const id = hub?.id ?? hub?.hub_id;
    if (!selected.has(String(id))) continue;
    for (const type of normalizeHubDispatchTypes(hub)) {
      types.add(type);
    }
  }
  return types;
}

/**
 * Filter locations whose dispatch types intersect the source set.
 * ITCL-only From → locations that include ITCL (not OGCL-only).
 * OGCL destinations without ITCL require an OGCL Transfer Request.
 */
export function filterHubsByDispatchIntersection(hubs, sourceDispatchTypes) {
  const source = sourceDispatchTypes instanceof Set
    ? sourceDispatchTypes
    : new Set(
        (Array.isArray(sourceDispatchTypes) ? sourceDispatchTypes : [])
          .map((t) => String(t).trim().toUpperCase())
          .filter(Boolean),
      );
  if (!source.size) return Array.isArray(hubs) ? hubs : [];
  return (hubs || []).filter((hub) =>
    normalizeHubDispatchTypes(hub).some((type) => source.has(type)),
  );
}

/**
 * ML "To" locations for selected From hubs.
 * Default: branches only + dispatch intersection.
 * When From hub(s) have destination mapping: mapped hubs + child branches
 * (minus source), then dispatch intersection still applies.
 */
export function toLocationsForMlDispatch(hubs, fromLocationIds, { enabled = true } = {}) {
  const fromIds = fromLocationIds || [];
  const allRestricted = fromIds.length > 0 && fromIds.every((id) => {
    const hub = (hubs || []).find((h) => String(h.id) === String(id));
    return resolveAllowedDestinationHubIds(hub, hubs) != null;
  });

  let basePool = allRestricted
    ? filterToLocationsByDestinationMapping(hubs || [], fromIds, hubs)
    : filterBranchLocations(hubs);

  if (!enabled || !fromIds.length) {
    return basePool;
  }
  const sourceTypes = unionDispatchTypesForHubIds(hubs, fromIds);
  return filterHubsByDispatchIntersection(basePool, sourceTypes);
}
